import type { TableColumnsType } from "antd";
import { Empty, Table, TableProps } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import type { ColumnsType, TableRef } from "antd/es/table";
import mergeClassNames from "common/mergeClassNames";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import { useScreenSize } from "hooks";
import React from "react";
import type { ResizeCallbackData } from "react-resizable";
import ResizableTitle from "./ResizableTitle";
import "./style.css";

type IDataTableProps<T> = {
    fixedHeader?: boolean;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    showHeader?: boolean;
    showFooter?: boolean;
    dataTestId?: string;
    containerClassName?: string;
    contentClassName?: string;
    headerClassName?: string;
    tableProps?: TableProps<T>;
    hideSelectionColumn?: boolean;
    resizable?: boolean;
    children?: React.ReactNode;
    emptyText?: string;
    key?: string;
    autoScrollTo?: boolean;
    contentStyle?: React.CSSProperties;
    headerStyle?: React.CSSProperties;
    style?: React.CSSProperties;
};

/**
 * Wrapper for Antd Table to workaround the scrolling and pagination issues.
 */
export default function DataTable<T extends AnyObject>({
    fixedHeader = true,
    containerClassName,
    contentClassName,
    headerClassName,
    showHeader = true,
    header,
    dataTestId = "cypress-data-table-id-not-set",
    tableProps,
    hideSelectionColumn = false,
    resizable = false,
    children,
    emptyText = "No data",
    autoScrollTo = false,
    contentStyle,
    headerStyle,
    style,
    ...props
}: IDataTableProps<T>) {
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = React.useState<number>(0);

    const {
        columns: dataColumns,
        rowSelection: tableRowSelection,
        scroll: tableScroll,
        pagination: tablePagination,
        dataSource,
        loading: tableLoading,
        rowKey: tableRowKey,
        rowClassName: tableRowClassName,
        ...restTableProps
    } = tableProps || {};
    const [pagination, setPagination] = React.useState<{
        defaultPageSize: number;
        pageSize: number;
    }>({ defaultPageSize: 100, pageSize: 100, ...tablePagination });
    const [columns, setColumns] = React.useState<ColumnsType<T>>([]);

    const tableRef = React.useRef<TableRef>(null);
    const [tableHeight, setTableHeight] = React.useState<number | undefined>(0);
    const screenSize = useScreenSize(); // To track resize of window
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Detect change in content height
    React.useEffect(() => {
        if (!contentRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === contentRef.current) {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                    // Debounce height recalculation without showing a loading overlay —
                    // layout shifts (e.g. validation errors appearing) should not flash a spinner.
                    timeoutRef.current = setTimeout(() => {
                        setContentHeight(entry.contentRect.height);
                    }, 100);
                }
            }
        });

        resizeObserver.observe(contentRef.current);

        // Clean up the observer when the component unmounts
        return () => {
            resizeObserver.disconnect();
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = null;
        };
    }, []);

    React.useEffect(() => {
        // Automagically scroll to the row upon selection
        const delayedScroll = setTimeout(() => {
            if (autoScrollTo && tableRowSelection?.selectedRowKeys && tableRowSelection?.selectedRowKeys.length > 0 && tableRef?.current) {
                tableRef.current.scrollTo({
                    key: tableRowSelection.selectedRowKeys[0],
                });
            }
        }, 300); // Need some time buffer to let other actions happen first

        return () => clearTimeout(delayedScroll);
    }, [tableRowSelection?.selectedRowKeys, tableRef, autoScrollTo]);

    /**
     * Workaround to calculate the exact height available for scrolling in order to show the pagination.
     */
    React.useLayoutEffect(() => {
        setTableHeight(0); // Reset it first

        // Detect change in layout and calculate the height of container
        const container = contentRef.current ?? document;
        const paginationDiv = container.getElementsByClassName("data-table-pagination")[0];
        const tableHeaderDiv = container.getElementsByClassName("ant-table-header")[0];
        const tableBodyDiv = container.getElementsByClassName("ant-table-body")[0];

        const paginationHeight = paginationDiv?.clientHeight ?? 0;
        const tableHeaderHeight = tableHeaderDiv?.clientHeight ?? 0;

        // Calculate the pagination margin
        let paginationMargin = 0;
        if (paginationDiv) {
            const styles = window.getComputedStyle(paginationDiv);
            paginationMargin = Math.ceil(parseFloat(styles["marginTop"]) + parseFloat(styles["marginBottom"]));
        }

        // Calculate table body paddings, margins, borders
        let tableBodyExtra = 0;
        if (tableBodyDiv) {
            const styles = window.getComputedStyle(tableBodyDiv);
            tableBodyExtra =
                parseFloat(styles["paddingTop"]) +
                parseFloat(styles["paddingBottom"]) +
                parseFloat(styles["marginTop"]) +
                parseFloat(styles["marginBottom"]) +
                parseFloat(styles["borderTopWidth"]) +
                parseFloat(styles["borderBottomWidth"]);
        }

        setTableHeight(contentHeight - tableHeaderHeight - paginationHeight - paginationMargin - tableBodyExtra);
    }, [screenSize, contentHeight, dataSource?.length]); // re-measure when data/table arrives

    React.useEffect(() => {
        setIsLoading(true);
        setIsLoading(false);
    }, [tableProps]);

    const scroll = React.useMemo(
        () => ({
            y: fixedHeader ? tableHeight : undefined,
            x: "max-content",
            scrollToFirstRowOnChange: true,
            ...tableScroll,
        }),
        [fixedHeader, tableHeight, tableScroll],
    );

    // For resizing columns
    const handleResizeStop = React.useCallback(
        (index: number) =>
            (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
                const newColumns = [...columns];
                newColumns[index] = {
                    ...newColumns[index],
                    width: size.width,
                };
                setColumns(newColumns);
            },
        [columns],
    );

    const mergedColumns = React.useMemo(
        () =>
            columns.map<TableColumnsType<T>[number]>((col, index) => ({
                ...col,
                onHeaderCell:
                    !!resizable && !col.fixed
                        ? (column: TableColumnsType<T>[number]) => ({
                              width: column.width,
                              onResizeStop: handleResizeStop(index) as React.ReactEventHandler<any>,
                          })
                        : undefined,
            })),
        [columns, handleResizeStop, resizable],
    );

    React.useEffect(() => {
        // Ensuring that the change in props columns are updated in table
        setColumns(dataColumns ?? []);
    }, [dataColumns]);

    const getRowKey = React.useCallback(
        (record: T, index?: number): React.Key => {
            if (typeof tableRowKey === "function") {
                return tableRowKey(record, index);
            }

            if (typeof tableRowKey === "string") {
                return record[tableRowKey] as React.Key;
            }

            return (record.key as React.Key) ?? String(index);
        },
        [tableRowKey],
    );

    const getRowClassName = React.useCallback(
        (record: T, index: number, indent: number) => {
            const selectedRowKey = getRowKey(record, index);
            const selectedRowClassName = tableRowSelection?.selectedRowKeys?.includes(selectedRowKey) ? "ant-table-row-selected bg-selected-row cursor-pointer" : "cursor-pointer";
            const rowClassName = typeof tableRowClassName === "function" ? tableRowClassName(record, index, indent) : tableRowClassName;

            return mergeClassNames(selectedRowClassName, rowClassName);
        },
        [getRowKey, tableRowClassName, tableRowSelection?.selectedRowKeys],
    );

    return (
        <FlexContainerLayout
            ref={contentRef}
            containerClassName={mergeClassNames("gap-0 data-table", hideSelectionColumn ? "[&_.ant-table-selection-column]:!p-0" : "", containerClassName)}
            contentClassName={mergeClassNames("overflow-hidden data-table-content", contentClassName)}
            showHeader={showHeader && !!header}
            headerClassName={mergeClassNames("flex flex-row gap-4 data-table-header mb-6", headerClassName)}
            header={header && (typeof header === "string" ? <p className="text-xl font-bold">{header}</p> : header)}
            contentStyle={contentStyle}
            headerStyle={headerStyle}
            style={style}
            {...props}
        >
            {dataSource?.length ? (
                <Table<T>
                    ref={tableRef}
                    dataSource={dataSource}
                    columns={mergedColumns}
                    tableLayout="auto"
                    size="small"
                    sticky={tableProps?.sticky ?? fixedHeader}
                    pagination={{
                        hideOnSinglePage: true,
                        total: dataSource.length,
                        defaultPageSize: pagination.defaultPageSize,
                        pageSize: pagination.pageSize,
                        responsive: true,
                        showSizeChanger: false,
                        showTotal: (total, range) => (
                            <div>
                                {range[0]} - {range[1]} of total {total}
                            </div>
                        ),
                        className: "data-table-pagination gap-2",
                        onShowSizeChange: (_p, size) =>
                            setPagination((prev) => ({
                                ...prev,
                                pageSize: size,
                            })),
                        ...tablePagination,
                    }}
                    data-testid={dataTestId}
                    scroll={scroll}
                    rowSelection={hideSelectionColumn ? undefined : tableRowSelection}
                    rowKey={tableRowKey}
                    rowClassName={getRowClassName}
                    components={{
                        header: {
                            cell: ResizableTitle,
                        },
                    }}
                    {...restTableProps}
                />
            ) : (
                <div className="absolute flex size-full flex-col items-center justify-center gap-3 rounded-md bg-gray-50 text-gray-400">
                    <LoadingOverlay busy={isLoading || !!tableLoading} message="Loading..." spinner />
                    {!isLoading && !tableLoading && (
                        <div className="p-4">
                            <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        </div>
                    )}
                </div>
            )}
            {children}
        </FlexContainerLayout>
    );
}
